import React, { useRef, useState } from "react";
import { UploadCloud, FileText, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { adminApi } from "../lib/api";

const TYPES = ["loi", "decret", "arrete", "circulaire", "ordonnance"];
const STATUSES = ["en_vigueur", "modifie", "abroge"];

export default function AdminUpload() {
  const { t } = useI18n();
  const [tab, setTab] = useState("pdf");
  const [file, setFile] = useState(null);
  const [rawText, setRawText] = useState("");
  const [meta, setMeta] = useState({
    title: "",
    document_type: "decret",
    reference: "",
    date_publication: "",
    date_entree_vigueur: "",
    status: "en_vigueur",
  });
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    setSubmitting(true);
    setProgress(10);
    try {
      if (tab === "pdf") {
        if (!file) throw new Error("No file selected");
        const fd = new FormData();
        fd.append("file", file);
        Object.entries(meta).forEach(([k, v]) => v && fd.append(k, v));
        // Simulated progress while OCR runs
        const interval = setInterval(() => {
          setProgress((p) => (p < 90 ? p + 7 : p));
        }, 900);
        try {
          const { data } = await adminApi.ingestPdf(fd);
          setResult(data);
          setProgress(100);
        } finally {
          clearInterval(interval);
        }
      } else {
        setProgress(40);
        const { data } = await adminApi.ingestText(meta, rawText);
        setResult(data);
        setProgress(100);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Ingestion failed");
      setProgress(0);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-upload-page">
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">
          / ingestion
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight">
          {t("uploadP.title")}
        </h1>
        <p className="text-sm text-foreground/60 mt-2 max-w-2xl">
          {t("uploadP.subtitle")}
        </p>
      </div>

      <div className="inline-flex border border-primary/20 rounded-sm overflow-hidden mb-6" data-testid="upload-tabs">
        {[
          { k: "pdf", label: t("uploadP.tabPdf") },
          { k: "text", label: t("uploadP.tabText") },
        ].map((x) => (
          <button
            key={x.k}
            type="button"
            onClick={() => setTab(x.k)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              tab === x.k
                ? "bg-primary text-white"
                : "bg-white text-foreground/70 hover:bg-primary/5"
            }`}
            data-testid={`tab-${x.k}`}
          >
            {x.label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="grid md:grid-cols-5 gap-6" data-testid="upload-form">
        {/* LEFT */}
        <div className="md:col-span-3 space-y-4">
          {tab === "pdf" ? (
            <>
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                className="border-2 border-dashed border-primary/30 hover:border-primary bg-primary/5 rounded-sm p-10 flex flex-col items-center justify-center cursor-pointer transition-all"
                data-testid="pdf-dropzone"
              >
                <UploadCloud size={36} className="text-primary/70 mb-3" />
                <p className="font-display text-lg text-primary font-semibold">
                  {t("uploadP.dropHere")}
                </p>
                <p className="text-sm text-foreground/60 mt-1">
                  {t("uploadP.orClick")}
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="pdf-file-input"
                />
                {file && (
                  <div className="mt-5 flex items-center gap-2 bg-white border border-primary/20 px-3 py-2 rounded-sm">
                    <FileText size={14} className="text-primary" />
                    <span className="text-sm font-mono text-foreground/80">
                      {file.name}
                    </span>
                    <span className="font-mono text-[10px] text-foreground/50">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <textarea
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              placeholder={t("uploadP.rawText")}
              rows={18}
              className="w-full bg-white border border-primary/20 rounded-sm p-4 text-sm font-mono leading-relaxed focus:border-primary outline-none"
              data-testid="raw-text-input"
              required
            />
          )}

          {/* Progress */}
          {submitting && (
            <div data-testid="upload-progress">
              <div className="flex items-center justify-between font-mono text-xs text-foreground/60 mb-1">
                <span>{t("uploadP.processing")}</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full h-1.5 bg-primary/10 rounded-sm overflow-hidden">
                <div
                  className="h-full bg-accent transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {result && (
            <div
              className="bg-[#D1FAE5] border border-[#A7F3D0] rounded-sm p-4 flex items-start gap-3"
              data-testid="upload-success"
            >
              <CheckCircle2 size={18} className="text-[#065F46] shrink-0 mt-0.5" />
              <div className="text-sm text-[#065F46] leading-relaxed">
                <div className="font-semibold">{t("uploadP.successTitle")}</div>
                <div className="font-mono text-xs mt-1">
                  chunks={result.chunks_count} · quality=
                  {(result.quality_score * 100).toFixed(0)}%
                </div>
              </div>
            </div>
          )}

          {error && (
            <div
              className="bg-[#FEE2E2] border border-[#FECACA] rounded-sm p-4 flex items-start gap-3"
              data-testid="upload-error"
            >
              <AlertCircle size={18} className="text-[#991B1B] shrink-0 mt-0.5" />
              <div className="text-sm text-[#991B1B]">
                <div className="font-semibold">{t("uploadP.errorTitle")}</div>
                <div className="font-mono text-xs mt-1">{error}</div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — metadata */}
        <div className="md:col-span-2 bg-white border border-primary/15 rounded-sm p-5 space-y-4 h-fit">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent mb-2">
            / {t("uploadP.metadata")}
          </div>

          <TextInput
            label={t("uploadP.fTitle")}
            value={meta.title}
            onChange={(v) => setMeta({ ...meta, title: v })}
            required
            testid="meta-title-input"
          />
          <SelectInput
            label={t("uploadP.fType")}
            value={meta.document_type}
            onChange={(v) => setMeta({ ...meta, document_type: v })}
            options={TYPES.map((x) => ({ value: x, label: t(`types.${x}`) }))}
            testid="meta-type-select"
          />
          <TextInput
            label={t("uploadP.fReference")}
            value={meta.reference}
            onChange={(v) => setMeta({ ...meta, reference: v })}
            required
            placeholder="Ex. Décret n°2024/0123/PM"
            testid="meta-reference-input"
          />
          <TextInput
            label={t("uploadP.fDatePub")}
            type="date"
            value={meta.date_publication}
            onChange={(v) => setMeta({ ...meta, date_publication: v })}
            testid="meta-date-pub-input"
          />
          <TextInput
            label={t("uploadP.fDateEntry")}
            type="date"
            value={meta.date_entree_vigueur}
            onChange={(v) => setMeta({ ...meta, date_entree_vigueur: v })}
            testid="meta-date-entry-input"
          />
          <SelectInput
            label={t("uploadP.fStatus")}
            value={meta.status}
            onChange={(v) => setMeta({ ...meta, status: v })}
            options={STATUSES.map((x) => ({ value: x, label: t(`status.${x}`) }))}
            testid="meta-status-select"
          />

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white py-3 rounded-sm font-medium hover:bg-primary-light transition-colors disabled:opacity-60"
            data-testid="upload-submit-btn"
          >
            {submitting ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />}
            {submitting ? t("uploadP.processing") : t("uploadP.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}

function TextInput({ label, testid, ...rest }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 block mb-1.5">
        {label}
      </span>
      <input
        className="w-full bg-white border border-primary/20 rounded-sm px-3 py-2 text-sm focus:border-primary outline-none"
        data-testid={testid}
        {...rest}
        onChange={(e) => rest.onChange(e.target.value)}
      />
    </label>
  );
}

function SelectInput({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 block mb-1.5">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-white border border-primary/20 rounded-sm px-3 py-2 text-sm focus:border-primary outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
